import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';

export function SettingsPage(): React.ReactElement {
  const { user, logout } = useAuth();

  const formattedDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  const initial = (user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase();
  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="max-w-4xl space-y-6 animate-slide-in">
      {/* Header card */}
      <div className="relative overflow-hidden glass-card p-6 border-white/10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-primary-900/50 shrink-0">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white leading-tight truncate">
              {user?.name || 'IELTS Learner'}
            </h2>
            <p className="text-sm text-slate-400 truncate">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              {isAdmin ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Administrator
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-500/10 text-primary-400 border border-primary-500/20">
                  Active Account
                </span>
              )}
            </div>
          </div>
          <Button
            id="settings-logout-btn"
            variant="ghost"
            onClick={() => void logout()}
            className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 border border-rose-500/20 shrink-0"
          >
            Sign out
          </Button>
        </div>
      </div>

      {/* Account Details */}
      <Card padding="md">
        <h3 className="text-base font-semibold text-white mb-4">Account Information</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <dt className="text-xs font-medium text-slate-400">User ID</dt>
            <dd className="mt-1 text-sm font-mono text-slate-200 break-all">{user?.id ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400">Email Address</dt>
            <dd className="mt-1 text-sm text-slate-200 break-all">{user?.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400">Display Name</dt>
            <dd className="mt-1 text-sm text-slate-200">{user?.name || '(Not set)'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400">Member Since</dt>
            <dd className="mt-1 text-sm text-slate-200">{formattedDate}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400">Role</dt>
            <dd className="mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                isAdmin
                  ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20'
                  : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
              }`}>
                {user?.role ?? 'USER'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-400">Account Status</dt>
            <dd className="mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                ACTIVE
              </span>
            </dd>
          </div>
        </dl>
      </Card>

      {/* Security & Authentication */}
      <Card padding="md">
        <h3 className="text-base font-semibold text-white mb-4">Security &amp; Session</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-white/8">
            <div>
              <p className="text-sm font-medium text-white">Authentication Method</p>
              <p className="text-xs text-slate-400">JSON Web Token (JWT) with bcrypt password hashing</p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Secured
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-white">Session Status</p>
              <p className="text-xs text-slate-400">Currently active on this device</p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Active
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}
